<template>
  <div class="report-wrapper-full-width">
    <div class="tabs-container">
      <div
        class="tab"
        :class="{ 'active': activeTab === 'ReportSummary' }"
        @click="changeTab('ReportSummary')"
      >
        Report Summary
      </div>
      <div
        class="tab"
        :class="{ 'active': activeTab === 'MapViewer' }"
        @click="changeTab('MapViewer')"
      >
        Map Viewer
      </div>
      <div
        class="tab"
        :class="{ 'active': activeTab === 'Datalog' }"
        @click="changeTab('Datalog')"
      >
        Datalog
      </div>
      <div
        class="tab"
        :class="{ 'active': activeTab === 'CustomChart' }"
        @click="changeTab('CustomChart')"
      >
        Custom Chart
      </div>
      <div
        class="tab"
        :class="{ 'active': activeTab === 'RpmClassification' }"
        @click="changeTab('RpmClassification')"
        v-if="canRead('summary_operation_mode')"
      >
        RPM Classification
      </div>
      <div
        class="tab"
        :class="{ 'active': activeTab === 'SatelliteDatalog' }"
        @click="changeTab('SatelliteDatalog')"
        v-if="hasSatelliteAssets"
      >
        Satellite Datalog
      </div>
      <div
        class="tab"
        :class="{ 'active': activeTab === 'TeltonikaMap' }"
        @click="changeTab('TeltonikaMap')"
        v-if="hasTeltonikaAccess"
      >
        Teltonika Map
      </div>
      <div
        class="tab"
        :class="{ 'active': activeTab === 'AlertHistory' }"
        @click="changeTab('AlertHistory')"
        v-if="canRead('alert_incident')"
      >
        Alert History
      </div>
    </div>

    <div class="tab-content">
      <!--
        key di sini WAJIB ada -- tanpanya Vue 2 nge-reuse instance <report-summary>
        yang sama buat kedua tab, mounted() gak pernah re-fire pas pindah tab,
        dan tab rollup diem-diem nampilin data basi tanpa gejala kelihatan.
        Jangan dihapus pas tidy-up.
      -->
      <div v-if="activeTab === 'ReportSummary'" key="ReportSummary">
        <report-summary />
      </div>
      <div v-if="activeTab === 'MapViewer'">
        <map-viewer />
      </div>
      <div v-if="activeTab === 'Datalog'">
        <fms-datalog />
      </div>
      <div v-if="activeTab === 'CustomChart'">
        <chart-report />
      </div>
      <div v-if="activeTab === 'RpmClassification' && canRead('summary_operation_mode')">
        <operation-mode-summary />
      </div>
      <div v-if="activeTab === 'SatelliteDatalog'">
        <satellite-datalog />
      </div>
      <div v-if="activeTab === 'TeltonikaMap' && hasTeltonikaAccess">
        <teltonika-map />
      </div>
      <div v-if="activeTab === 'AlertHistory' && canRead('alert_incident')">
        <alert-history />
      </div>
    </div>
  </div>
</template>

<script>
import ReportSummary from '@/views/report/FmsReport.vue';
import MapViewer from '@/views/map_viewer/Index.vue';
import FmsDatalog from '@/views/report/FmsReportDatalog.vue';
import TeltonikaMap from '@/views/map/TeltonikaMap.vue';
import ChartReport from '@/views/report/ChartReport.vue';
import OperationModeSummary from '@/views/report/OperationModeSummary.vue';
import SatelliteDatalog from '@/views/report/SatelliteDatalog.vue';
import AlertHistory from '@/views/alert/AlertHistory.vue';
import { mapState } from 'vuex';
import AccessMixin from '@/components/auth/AccessMixin';

// Tautan lama tetap kebuka, tapi URL-nya langsung dinormalisasi ke MapViewer.
const TAB_LAMA = 'VoyageReport';

export default {
  name: 'ReportWrapper',
  mixins: [AccessMixin],
  components: {
    ReportSummary,
    MapViewer,
    FmsDatalog,
    TeltonikaMap,
    ChartReport,
    OperationModeSummary,
    SatelliteDatalog,
    AlertHistory,
  },
  computed: {
    ...mapState('asset', ['assets']),
    ...mapState('auth', ['userAccess']),
    // Mengambil state tab aktif dari URL sebagai "single source of truth"
    activeTab() {
      const menu = this.$route.query.menu === TAB_LAMA ? 'MapViewer' : this.$route.query.menu;
      const validTabs = ['ReportSummary', 'MapViewer', 'Datalog', 'CustomChart', 'SatelliteDatalog'];

      if (this.hasTeltonikaAccess) {
        validTabs.push('TeltonikaMap');
      }

      if (this.canRead('summary_operation_mode')) {
        validTabs.push('RpmClassification');
      }

      if (this.canRead('alert_incident')) {
        validTabs.push('AlertHistory');
      }

      // Remove SatelliteDatalog from valid tabs if no satellite assets
      if (!this.hasSatelliteAssets) {
        const satelliteIndex = validTabs.indexOf('SatelliteDatalog');
        if (satelliteIndex > -1) {
          validTabs.splice(satelliteIndex, 1);
        }
      }

      // Jika menu di URL valid, gunakan itu. Jika tidak, gunakan default.
      if (menu && validTabs.includes(menu)) {
        return menu;
      }

      return 'ReportSummary'; // Tab default
    },
    // Computed property untuk membersihkan duplikasi logika di template
    hasTeltonikaAccess() {
      return this.userAccess && this.userAccess.teltonika_map && this.userAccess.teltonika_map.find((x) => x === 'read');
    },
    hasSatelliteAssets() {
      // Check if at least one asset has massSateliteId
      return this.assets && this.assets.some((asset) => asset.massSateliteId && asset.massSateliteId !== '' && asset.massSateliteId !== 'null' && asset.massSateliteId !== 'undefined');
    },
  },
  watch: {
    '$route.query.menu': {
      immediate: true,
      handler(menu) {
        if (menu !== TAB_LAMA) return;
        this.$router.replace({ query: { ...this.$route.query, menu: 'MapViewer' } }).catch(() => {});
      },
    },
  },
  methods: {
    // Method ini dipanggil saat tab diklik
    changeTab(tabName) {
      // Hanya update URL jika tab yang diklik berbeda dengan yang aktif
      if (this.activeTab !== tabName) {
        this.$router.push({ query: { menu: tabName } }).catch((err) => {
          // Mengabaikan error "NavigationDuplicated" yang wajar terjadi
          if (
            err.name !== 'NavigationDuplicated'
            && !err.message.includes('Avoided redundant navigation')
          ) {
            console.error(err);
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.report-wrapper-full-width {
  width: 100%;
}

.tabs-container {
  display: flex;
  overflow-x: auto;
  border-bottom: 2px solid #e0e0e0;
  width: 100%;
}

.tab {
  padding: 10px 18px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: #555;
  position: relative;
  transition: color 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
}

.tab:not(:last-child) {
  margin-right: 12px;
}

.tab:hover {
  color: #ff69b4;
}

.tab.active {
  color: #ff69b4;
  font-weight: 600;
}

.tab.active::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #ff69b4;
  border-radius: 2px;
}
</style>
